import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { agendaPublicService } from '../../services/agendaPublicService';
import {
    FaCalendarAlt,
    FaClock,
    FaMapMarkerAlt,
    FaImage,
    FaSpinner,
    FaStar,
    FaArrowRight,
    FaExclamationTriangle
} from 'react-icons/fa';

const AgendaTerkini = () => {
    const [agendaTerkini, setAgendaTerkini] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        loadAgenda();
    }, []);

    const loadAgenda = async () => {
        setLoading(true);
        setError('');

        // ✅ Ambil 3 agenda akan datang
        const result = await agendaPublicService.getAgendas({
            waktu: 'akan_datang',
            per_page: 3,
        });

        if (result.success) {
            const data = result.data.data || result.data || [];
            setAgendaTerkini(Array.isArray(data) ? data.slice(0, 3) : []);
        } else {
            setError(result.message);
        }
        setLoading(false);
    };

    const getImageUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return path;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    };

    const formatShortDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'akan_datang': return 'bg-blue-100 text-blue-700';
            case 'berlangsung': return 'bg-green-100 text-green-700';
            case 'selesai': return 'bg-gray-100 text-gray-600';
            case 'dibatalkan': return 'bg-red-100 text-red-700';
            default: return 'bg-gray-100 text-gray-600';
        }
    };

    const getStatusLabel = (status) => {
        const labels = {
            akan_datang: 'Akan Datang',
            berlangsung: 'Berlangsung',
            selesai: 'Selesai',
            dibatalkan: 'Dibatalkan',
        };
        return labels[status] || status;
    };

    const stripHtml = (html) => {
        if (!html) return '';
        return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    };

    const truncateText = (text, maxLength = 120) => {
        if (!text) return '';
        return text.length > maxLength
            ? text.substring(0, maxLength) + '...'
            : text;
    };

    // ============ LOADING STATE ============
    if (loading) {
        return (
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4">
                    <h2 className="section-title">Agenda Terkini BPKAD</h2>
                    <div className="text-center py-12">
                        <FaSpinner className="animate-spin text-4xl text-[#2E7D32] mx-auto mb-4" />
                        <p className="text-gray-500">Memuat agenda...</p>
                    </div>
                </div>
            </section>
        );
    }

    // ============ ERROR STATE ============
    if (error) {
        return (
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4">
                    <h2 className="section-title">Agenda Terkini BPKAD</h2>
                    <div className="text-center py-12">
                        <FaExclamationTriangle className="text-5xl text-yellow-500 mx-auto mb-4" />
                        <p className="text-gray-500 mb-4">{error}</p>
                        <button onClick={loadAgenda} className="btn-primary">
                            Coba Lagi
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    // ============ EMPTY STATE ============
    if (agendaTerkini.length === 0) {
        return (
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4">
                    <h2 className="section-title">Agenda Terkini BPKAD</h2>
                    <div className="text-center py-12">
                        <FaCalendarAlt className="text-6xl text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">Belum ada agenda terkini</p>
                    </div>
                </div>
            </section>
        );
    }

    // ============ AGENDA LIST ============
    return (
        <section className="py-16 bg-gray-50">
            <div className="container mx-auto px-4">
                <div className="flex items-center justify-between mb-8">
                    <h2 className="section-title !mb-0 !text-left">Agenda Terkini BPKAD</h2>
                    <Link
                        to="/informasi/agenda"
                        className="hidden md:flex items-center gap-2 text-[#2E7D32] hover:text-[#4CAF50] font-semibold transition-colors"
                    >
                        Lihat Semua
                        <FaArrowRight />
                    </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                    {agendaTerkini.map((agenda) => (
                        <article
                            key={agenda.id}
                            className="card-glass overflow-hidden hover:shadow-xl transition-all duration-300 group flex flex-col"
                        >
                            {/* Thumbnail */}
                            <div className="relative overflow-hidden h-48 bg-gray-100">
                                {agenda.gambar ? (
                                    <img
                                        src={getImageUrl(agenda.gambar)}
                                        alt={agenda.judul}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        onError={(e) => {
                                            e.target.src = 'https://placehold.co/400x300/0F4C3A/FFFFFF?text=Agenda';
                                        }}
                                    />
                                ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-[#0F4C3A] to-[#2E7D32] flex items-center justify-center">
                                        <FaCalendarAlt className="text-5xl text-white/50" />
                                    </div>
                                )}

                                {/* Date Badge */}
                                <div className="absolute top-3 left-3 bg-white rounded-lg shadow-lg px-3 py-2 text-center min-w-[60px]">
                                    <p className="text-2xl font-bold text-[#0F4C3A] leading-none">
                                        {new Date(agenda.tanggal_mulai).getDate()}
                                    </p>
                                    <p className="text-xs text-gray-500 uppercase mt-1">
                                        {new Date(agenda.tanggal_mulai).toLocaleDateString('id-ID', { month: 'short' })}
                                    </p>
                                </div>

                                {/* Badge Penting */}
                                {agenda.is_penting && (
                                    <div className="absolute top-3 right-3">
                                        <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full flex items-center shadow">
                                            <FaStar className="mr-1" /> Penting
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Content */}
                            <div className="p-6 flex flex-col flex-1">
                                {/* Status & Kategori */}
                                <div className="flex flex-wrap items-center gap-2 mb-3">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(agenda.status_kegiatan)}`}>
                                        {getStatusLabel(agenda.status_kegiatan)}
                                    </span>
                                    <span className="bg-[#2E7D32]/10 text-[#2E7D32] px-2 py-1 rounded-full text-xs font-medium capitalize">
                                        {agenda.kategori}
                                    </span>
                                </div>

                                {/* Judul */}
                                <h3 className="text-lg font-bold text-[#0F4C3A] mb-3 group-hover:text-[#2E7D32] transition-colors line-clamp-2">
                                    {agenda.judul}
                                </h3>

                                {/* Info Waktu & Lokasi */}
                                <div className="space-y-1.5 mb-3 text-sm text-gray-600">
                                    <div className="flex items-center gap-2">
                                        <FaCalendarAlt className="text-[#2E7D32] text-xs flex-shrink-0" />
                                        <span>{formatShortDate(agenda.tanggal_mulai)}</span>
                                    </div>
                                    {agenda.jam_mulai && (
                                        <div className="flex items-center gap-2">
                                            <FaClock className="text-[#2E7D32] text-xs flex-shrink-0" />
                                            <span>{agenda.jam_mulai} WIB</span>
                                        </div>
                                    )}
                                    {agenda.lokasi && (
                                        <div className="flex items-center gap-2">
                                            <FaMapMarkerAlt className="text-[#2E7D32] text-xs flex-shrink-0" />
                                            <span className="line-clamp-1">{agenda.lokasi}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Deskripsi */}
                                <p className="text-gray-600 text-sm mb-4 line-clamp-2 flex-1">
                                    {truncateText(stripHtml(agenda.deskripsi), 120)}
                                </p>

                                {/* Read More */}
                                <Link
                                    to={`/informasi/agenda/${agenda.slug}`}
                                    className="text-[#2E7D32] hover:text-[#4CAF50] font-semibold flex items-center gap-2 mt-auto transition-colors"
                                >
                                    Lihat Detail
                                    <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform" />
                                </Link>
                            </div>
                        </article>
                    ))}
                </div>

                {/* Lihat Semua - Mobile */}
                <div className="text-center mt-8 md:hidden">
                    <Link
                        to="/informasi/agenda"
                        className="inline-flex items-center gap-2 text-[#2E7D32] hover:text-[#4CAF50] font-semibold"
                    >
                        Lihat Semua Agenda
                        <FaArrowRight />
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default AgendaTerkini;